import { useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Eyebrow, FadeUp } from "@/components/Reveal";

const FORMSPREE_ID = "";

export default function Enterprise() {
  const { t } = useLang();
  const [sent, setSent] = useState(false);
  const offerings = t("enterprise.offerings");
  const labels = t("enterprise.labels");
  const placeholders = t("enterprise.placeholders");
  const options = t("enterprise.options");

  const handleSubmit = (e) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    if (FORMSPREE_ID) {
      fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: d,
      });
    } else {
      const subject = encodeURIComponent(`Enterprise enquiry — ${d.get("interest")}`);
      const body = encodeURIComponent(
        `Name: ${d.get("name")}\nOrganization: ${d.get("organization")}\nEmail: ${d.get("email")}\nInterest: ${d.get("interest")}\n\n${d.get("message")}`
      );
      window.location.href = `mailto:site@vlrsoftware.com?subject=${subject}&body=${body}`;
    }
    setSent(true);
  };

  return (
    <div data-testid="enterprise-page" className="bg-paper pt-28 md:pt-36">
      <div className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10 md:pb-32">
        <FadeUp><Eyebrow>{t("enterprise.eyebrow")}</Eyebrow></FadeUp>
        <FadeUp delay={0.1}>
          <h1 className="mt-8 max-w-3xl font-serif text-5xl font-medium leading-[1.02] tracking-tight text-ink md:text-7xl">
            {t("enterprise.title")}
          </h1>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/60 md:text-lg">
            {t("enterprise.body")}
          </p>
        </FadeUp>

        <div className="mt-20 grid grid-cols-1 gap-16 lg:grid-cols-2">
          <div>
            {offerings.map((o, i) => (
              <FadeUp key={o.n} delay={0.1 * i} amount={0.4}>
                <div data-testid={`enterprise-offering-${o.n}`} className="border-t border-ink/10 py-8 last:border-b">
                  <div className="flex items-baseline gap-6">
                    <span className="font-sans text-4xl font-light text-cobalt">{o.n}</span>
                    <div>
                      <h2 className="font-serif text-2xl font-medium tracking-tight text-ink md:text-3xl">{o.title}</h2>
                      <p className="mt-3 max-w-md text-sm leading-relaxed text-ink/60">{o.body}</p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
            <FadeUp delay={0.3} className="mt-10">
              <p className="text-xs uppercase tracking-[0.25em] text-ink/40">{t("enterprise.scaleNote")}</p>
            </FadeUp>
          </div>

          <FadeUp delay={0.2} amount={0.1}>
            <form onSubmit={handleSubmit} data-testid="enterprise-form" className="border border-ink/10 bg-white p-7 md:p-10">
              <h2 className="font-serif text-2xl font-medium tracking-tight text-ink">{t("enterprise.formTitle")}</h2>
              <p className="mt-2 text-sm text-ink/50">{t("enterprise.formNote")}</p>

              <div className="mt-8 space-y-5">
                <div>
                  <label htmlFor="ent-name" className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink/50">{labels.name}</label>
                  <input id="ent-name" name="name" required data-testid="enterprise-form-name"
                    className="mt-2 w-full border border-ink/15 bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink/30 focus:border-cobalt"
                    placeholder={placeholders.name} />
                </div>
                <div>
                  <label htmlFor="ent-org" className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink/50">{labels.organization}</label>
                  <input id="ent-org" name="organization" data-testid="enterprise-form-organization"
                    className="mt-2 w-full border border-ink/15 bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink/30 focus:border-cobalt"
                    placeholder={placeholders.organization} />
                </div>
                <div>
                  <label htmlFor="ent-email" className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink/50">{labels.email}</label>
                  <input id="ent-email" name="email" type="email" required data-testid="enterprise-form-email"
                    className="mt-2 w-full border border-ink/15 bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink/30 focus:border-cobalt"
                    placeholder={placeholders.email} />
                </div>
                <div>
                  <label htmlFor="ent-interest" className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink/50">{labels.interest}</label>
                  <select id="ent-interest" name="interest" data-testid="enterprise-form-interest"
                    className="mt-2 w-full border border-ink/15 bg-paper px-4 py-3 text-sm text-ink focus:border-cobalt">
                    {options.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="ent-message" className="text-[11px] font-medium uppercase tracking-[0.2em] text-ink/50">{labels.message}</label>
                  <textarea id="ent-message" name="message" rows={4} required data-testid="enterprise-form-message"
                    className="mt-2 w-full resize-none border border-ink/15 bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink/30 focus:border-cobalt"
                    placeholder={placeholders.message} />
                </div>
              </div>

              <button type="submit" data-testid="enterprise-form-submit"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-ink active:scale-[0.99]">
                {t("enterprise.submit")}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>

              {sent && (
                <p data-testid="enterprise-form-confirmation" className="mt-4 flex items-center gap-2 text-sm font-medium text-cobalt">
                  <Check className="h-4 w-4" />
                  {t("enterprise.confirmation")}
                </p>
              )}
            </form>
          </FadeUp>
        </div>
      </div>
    </div>
  );
}
